// src/utils/countryDetect.js
// Detects visitor country once on app load and caches it.
// Priority: localStorage (user selected) → IP lookup → browser timezone → 'GL'
//
// Usage:
//   import { getCountry, initCountry } from './utils/countryDetect';
//
//   // Call once at app boot (App.jsx)
//   await initCountry();
//
//   // Then anywhere in the app
//   const country = getCountry(); // → 'VN', 'US', 'GL' etc.

const STORAGE_KEY = 'jt_country';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

let _country = null;

// ── Timezone → country fallback (no network needed) ──────────────────────────
const TIMEZONE_MAP = {
  'Asia/Ho_Chi_Minh':    'VN',
  'Asia/Hanoi':          'VN',
  'Asia/Saigon':         'VN',
  'America/New_York':    'US',
  'America/Chicago':     'US',
  'America/Denver':      'US',
  'America/Los_Angeles': 'US',
  'America/Toronto':     'CA',
  'America/Vancouver':   'CA',
  'Europe/London':       'GB',
  'Europe/Paris':        'FR',
  'Europe/Berlin':       'DE',
  'Asia/Singapore':      'SG',
  'Asia/Tokyo':          'JP',
  'Asia/Seoul':          'KR',
  'Asia/Bangkok':        'TH',
  'Asia/Jakarta':        'ID',
  'Asia/Kuala_Lumpur':   'MY',
  'Asia/Manila':         'PH',
  'Asia/Shanghai':       'CN',
  'Asia/Hong_Kong':      'HK',
  'Asia/Taipei':         'TW',
  'Australia/Sydney':    'AU',
  'Australia/Melbourne': 'AU',
  'Asia/Dubai':          'AE',
  'Asia/Riyadh':         'SA',
  'America/Sao_Paulo':   'BR',
  'America/Mexico_City': 'MX',
  'Pacific/Auckland':    'NZ',
  'Asia/Kolkata':        'IN',
};

function countryFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_MAP[tz] || null;
  } catch {
    return null;
  }
}

// ── IP-based lookup (free, no key needed) ────────────────────────────────────
async function countryFromIP() {
  try {
    // Cloudflare trace — fast, no API key, no rate limit
    const res = await fetch('https://cloudflare.com/cdn-cgi/trace', {
      signal: AbortSignal.timeout(3000),
    });
    const text = await res.text();
    const match = text.match(/loc=([A-Z]{2})/);
    if (match) return match[1];

    // Fallback: ipapi.co
    const res2 = await fetch('https://ipapi.co/country/', {
      signal: AbortSignal.timeout(3000),
    });
    const code = (await res2.text()).trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(code)) return code;
  } catch {
    // Network error or timeout — fall through
  }
  return null;
}

// ── Read/write cache ──────────────────────────────────────────────────────────
function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { country, ts } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null; // expired
    return country;
  } catch {
    return null;
  }
}

function writeCache(country) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ country, ts: Date.now() }));
  } catch {
    // localStorage unavailable (SSR, private mode)
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Get the currently resolved country code.
 * Returns 'GL' if detection hasn't run yet or failed.
 */
export function getCountry() {
  return _country || readCache() || 'GL';
}

/**
 * Manually override the country (e.g. from a country picker UI).
 * Persists to localStorage so it survives page refreshes.
 */
export function setCountry(country) {
  const code = country.toUpperCase().trim();
  _country = code;
  writeCache(code);
}

/**
 * Initialize country detection. Call ONCE at app boot.
 * Resolves immediately from cache if available, otherwise detects.
 */
export async function initCountry() {
  // 1. Already detected this session
  if (_country) return _country;

  // 2. Valid cache in localStorage
  const cached = readCache();
  if (cached) {
    _country = cached;
    return cached;
  }

  // 3. IP lookup (most accurate)
  const fromIP = await countryFromIP();
  if (fromIP) {
    _country = fromIP;
    writeCache(fromIP);
    return fromIP;
  }

  // 4. Timezone fallback (no network needed)
  const fromTZ = countryFromTimezone();
  if (fromTZ) {
    _country = fromTZ;
    writeCache(fromTZ);
    return fromTZ;
  }

  // 5. Default to GL (global — safe neutral fallback)
  _country = 'GL';
  writeCache('GL');
  return 'GL';
}
